import { forwardRef, RefObject } from "react";
import Joi from "joi";
import { Heading, Box, Text, Stack } from "@chakra-ui/react";
import { captureException } from "@sentry/nextjs";
import axios from "axios";

import PhoneInput, { JoiPhoneValidator } from "components/Chakra/PhoneInput";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import Button from "components/Chakra/Button";
import AnimatedInput from "components/Chakra/AnimatedInput";
import Checkbox from "components/Chakra/Checkbox";
import CustomLink from "components/Chakra/CustomLink";
import { useAlertModal } from "modals/useAlertModal";
import { ILocationSuggestion } from "utils/geo";
import { useTracking } from "contexts/Tracking";
import VillageInput from "components/Chakra/VillageInput/VillageInput";
import useRouter from "hooks/useRouter";

type IFormValues = {
  fullName: string;
  village: ILocationSuggestion | null;
  phone: { prefix: string; number: string };
  email: string;
  hasNotification: boolean;
};

type ContactFormProps = {
  forwardedRef: RefObject<HTMLDivElement>;
};

const location = Joi.object({ id: Joi.string(), label: Joi.string() }).custom(
  (value, helpers) => {
    if (!value) {
      return helpers.error("any.invalid");
    }

    return value;
  },
);

const FormSchema = Joi.object({
  fullName: Joi.string().required(),
  phone: JoiPhoneValidator(true),
  village: location.required(),
  email: Joi.string().email({ tlds: false }).required(),
  hasNotification: Joi.boolean(),
});

const ContactForm = forwardRef<HTMLElement, ContactFormProps>(
  ({ forwardedRef }) => {
    const { trackEvent } = useTracking();
    const { query } = useRouter();
    const { openModal: openAlertModal } = useAlertModal();
    const {
      handleSubmit,
      registerWithError,
      watch,
      setValue,
      clearErrors,
      errors,
      reset,
    } = useForm<IFormValues>(FormSchema, {
      defaultValues: {
        email: "",
        phone: { number: "", prefix: "+420" },
        fullName: "",
        hasNotification: false,
      },
    });

    const phonePrexif = watch("phone.prefix");
    const phoneNumber = watch("phone.number");
    const watchVillage = watch("village");

    const handleData: OnFormSubmitType<IFormValues> = async ({
      email,
      fullName,
      hasNotification,
    }) => {
      const url = "https://hook.eu1.make.com/ae7mz3i4vmen6foxthy7hr34i8gkvp6h";
      const requestData = {
        jmeno: fullName,
        village_id: watchVillage,
        phone: phonePrexif + phoneNumber,
        email,
        IN_web_form_type: "Zájem o schůzku",
        IN_web_entry_page: "/idealni-najemce-lp/idealni-najemce.html",
        IN_web_submit_page: "/idealni-najemce-lp/idealni-najemce.html",
        send_email: hasNotification ? "on" : "off",
        source: query?.utm_source,
        medium: query?.utm_medium,
        campaign: query?.utm_campaign,
      };

      try {
        const res = await axios.post(
          url,
          {
            ...requestData,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: false,
          },
        );
        if (res.status !== 200) {
          openAlertModal({ title: "Něco se pokazilo.", animation: "error" });
        } else {
          openAlertModal({
            title: "Děkujeme za odeslání formáláře.",
            animation: "check",
          });
          reset({
            email: "",
            fullName: "",
            phone: { prefix: "+420", number: "" },
            village: { id: "", label: "" },
          });
          trackEvent({
            "dl.eCat": "landing",
            "dl.eAct": "conversion",
            "dl.eLab": "Domluvit schůzku",
          });
          setValue("village", { id: "", label: "" });
        }
      } catch (error) {
        setValue("phone.number", phoneNumber);
        setValue("phone.prefix", phonePrexif);
        openAlertModal({ title: "Něco se pokazilo.", animation: "error" });
        captureException(error);
      }
    };

    return (
      <Stack
        ref={forwardedRef}
        id="idealni-najemce-form"
        margin={{ base: "0 auto", md: "0 auto" }}
        py={{ base: "40px", md: "60px" }}
        align="center"
      >
        <Heading mt="50px" mb="16px">
          Chcete o bezstarostném pronájmu zjistit víc?
        </Heading>
        <Text pb="40px" maxW="600px" textAlign="center">
          Nechejte nám na sebe kontakt a my se vám do 24 hodin ozveme. Rádi s
          vámi
          <Text as="span" fontWeight="700" color="ud-black">
            {" "}
            probereme vaše potřeby a představíme výhody naší služby.
          </Text>
        </Text>
        <Box
          maxW="380px"
          as="form"
          onSubmit={handleSubmit(handleData)}
          data-test="loginModal.signIn.form"
        >
          <AnimatedInput
            {...registerWithError("fullName")}
            type="text"
            label="Jméno a Příjmení"
            required
            data-test="offerDetail.contactIN.name"
          />
          <Box pb="11px">
            <VillageInput
              name="village"
              withBorders
              placeholder="Vyberte město *"
              // @ts-expect-error fix
              error={errors?.village}
              isNewInsertForm
              value={watchVillage ?? { id: "", label: "" }}
              onInputChange={(value) => {
                setValue(
                  "village",
                  { id: "", label: value },
                  { shouldValidate: true },
                );
              }}
              onLocationSelect={(loc) => {
                setValue("village", loc, { shouldValidate: true });
              }}
              isDefaultLocations
            />
          </Box>
          <Stack>
            <PhoneInput
              label="Telefon"
              required
              error={errors.phone}
              setValue={setValue}
              clearErrors={clearErrors}
              selectBoxValue={phonePrexif}
              phoneNumberValue={phoneNumber}
            />
          </Stack>
          <AnimatedInput
            {...registerWithError("email")}
            type="email"
            required
            autoComplete="email"
            label="E-mail "
          />
          <Checkbox
            {...registerWithError("hasNotification")}
            label="Chci dostávat e-mailem rady a tipy o pronajímání bytů."
          />
          <Text fontSize="12px">
            Odesláním formuláře souhlasíte se{" "}
            <CustomLink to="/zpracovani-osobnich-udaju">
              zpracováním osobních údajů
            </CustomLink>
          </Text>
          <Stack align="center" mt="30px">
            <Button type="submit" borderRadius="full" w="fit-content">
              Domluvit schůzku
            </Button>
          </Stack>
        </Box>
      </Stack>
    );
  },
);

export default ContactForm;
